import { useEffect, useRef, useState } from 'react';
import { PipelineAPI } from '../../api/PipelineAPI';
import { IntegrationDisplayConfigFactory } from '../../factories/integrationFactory';
import { EntityDisplayConfigFactory } from '../../../shared/factory/entityDisplayConfigFactory';
import {
  CREATE_OBJECTS_DISPLAY_CONFIG_TEMPLATE_FIELDS,
  EDIT_OBJECTS_DISPLAY_CONFIG_TEMPLATE_FIELDS
} from '../../../shared/constants/objects';
import { useQuery } from '../../../request/hooks/useQuery';
import {
  IntegrationBannerAction,
  RawPipelineReviewContent,
  RawPipelineReviewSelectionResponse,
  IntegrationBanner
} from '../../../shared/interfaces/integration';
import { AsyncTaskStatus } from '../../../shared/interfaces/async-task';
import { EntityDisplayConfig } from '../../../shared/interfaces/entity-display-config';
import { getErrorMessageFromAxiosError } from '../../../request/utils';
import { GenericApiResponse } from '../../../shared/interfaces/generics';
import { EntityUIState } from '../../../request/interfaces/entity-ui-state';
import { EntityBannersFactory } from '../../../shared/components/EntityBanner/factory/entityBannersFactory';

interface HookState {
  uiState: EntityUIState;
  error: string | null;
  content: {
    sourceDisplayConfig: EntityDisplayConfig;
    destinationDisplayConfig: EntityDisplayConfig;
    integrationDisplayConfig: EntityDisplayConfig;
    objectsDisplayConfig: EntityDisplayConfig;
    banners: IntegrationBanner[];
  } | null;
}

export function usePipelineReview(pipelineId: number, isEditing: boolean) {
  const [state, setState] = useState<HookState>({
    uiState: EntityUIState.IDLE,
    error: null,
    content: null
  });
  const staticContentRef = useRef<HookState['content']>();

  const { fetch: fetchObjectSelectionDiff } = useQuery({
    dataSource$: args => PipelineAPI.getPipelineReviewSelection(pipelineId, args as string),
    pollInterval: 5000,
    pollUntil: res => {
      const {
        data: { status }
      } = res as unknown as GenericApiResponse<RawPipelineReviewSelectionResponse>;
      return status === AsyncTaskStatus.EXECUTING;
    },
    fetchSuccess: res => {
      const {
        data: { banners, fetch_review: content, status_reason: reason, status }
      } = res as unknown as GenericApiResponse<RawPipelineReviewSelectionResponse>;

      const isSuccessful = status === AsyncTaskStatus.EXECUTED;
      const isFailed = status === AsyncTaskStatus.FAILED;

      if (isSuccessful) {
        const { activated, deactivated, updated } = content;
        const objectsSelectionChanged = isEditing ? activated + deactivated + updated > 0 : false;

        const templateFields = isEditing
          ? EDIT_OBJECTS_DISPLAY_CONFIG_TEMPLATE_FIELDS
          : CREATE_OBJECTS_DISPLAY_CONFIG_TEMPLATE_FIELDS;

        setState(prevState => ({
          ...prevState,
          uiState: EntityUIState.IDLE,
          content: {
            ...staticContentRef.current!,
            objectsDisplayConfig: {
              ...EntityDisplayConfigFactory({
                name: '',
                template: {
                  fields: templateFields,
                  values: {
                    updated,
                    deleted: deactivated,
                    inserted: activated
                  }
                }
              }),
              hasDiff: objectsSelectionChanged
            },
            banners: EntityBannersFactory<IntegrationBannerAction>(banners ?? [])
          }
        }));
      }

      if (isFailed) {
        setState({
          uiState: EntityUIState.ERRORED,
          error: reason,
          content: null
        });
      }
    },
    fetchError: e => {
      setState({
        uiState: EntityUIState.ERRORED,
        error: getErrorMessageFromAxiosError(e),
        content: null
      });
    },
    terminatePollOnError: true
  });

  const fetchReviewContent = () => {
    setState(prevState => ({
      ...prevState,
      uiState: EntityUIState.LOADING
    }));

    const promises = [
      PipelineAPI.getPipelineReviewContent(pipelineId),
      PipelineAPI.submitPipelineReviewSelectionDiffRequest(pipelineId)
    ];

    Promise.all(promises)
      .then(([staticContent, selectionStatus]) => {
        const reviewPageId = (
          selectionStatus.data as {
            review_page_id: string;
          }
        ).review_page_id;

        staticContentRef.current = {
          ...IntegrationDisplayConfigFactory(staticContent.data as RawPipelineReviewContent),
          objectsDisplayConfig: {
            hasDiff: false,
            fields: []
          },
          banners: []
        };

        fetchObjectSelectionDiff(reviewPageId);
      })
      .catch(e => {
        setState(prevState => ({
          ...prevState,
          uiState: EntityUIState.ERRORED,
          error: getErrorMessageFromAxiosError(e)
        }));
      });
  };

  useEffect(() => {
    fetchReviewContent();
  }, []);

  return {
    fetchReviewContent,
    ...state
  };
}
